import { Button } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useConfirmClear } from '~/hooks/useConfirmClear';

interface AnnotationClearAllButtonProps {
    /** Length of the list being cleared; disarms the confirmation at zero. */
    itemCount: number;
    onClear: () => void;
    labelId: string;
    confirmLabelId: string;
    fullWidth?: boolean;
    /** Look while unarmed. The armed look is fixed, so it always reads alike. */
    variant?: 'text' | 'outlined';
}

/**
 * Two-step "Clear all" for an annotation list: the first click arms, the second
 * clears (docs/ui_specs.md: a destructive clinical action needs a confirmation
 * step). Owns {@link useConfirmClear} rather than taking its result, so the
 * armed state and the button that shows it cannot be wired up apart.
 */
export default function AnnotationClearAllButton({
    itemCount,
    onClear,
    labelId,
    confirmLabelId,
    fullWidth = false,
    variant = 'text',
}: AnnotationClearAllButtonProps) {
    const intl = useIntl();
    const { confirming, requestClear } = useConfirmClear(itemCount, onClear);
    return (
        <Button
            size="small"
            variant={confirming ? 'contained' : variant}
            color={confirming ? 'error' : 'inherit'}
            startIcon={<Trash2 size={14} />}
            onClick={requestClear}
            disabled={itemCount === 0}
            fullWidth={fullWidth}
        >
            {intl.formatMessage({ id: confirming ? confirmLabelId : labelId })}
        </Button>
    );
}
